/**
 * SlackNotificationsSection — org-wide Slack webhook for inventory-error
 * alerts (extracted from the old god component's `renderSlackNotifications`).
 *
 * The webhook URL is write-only (only a masked suffix round-trips), so it's
 * cleared on successful save like the other credential sections in this
 * area — the old god component left it populated post-save, which made the
 * section look permanently dirty under the new save-bar model.
 */
import { useEffect, useState } from 'react';
import { MessageSquare, Loader2, Send, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { trpc } from '@/lib/trpc';
import { SettingsSection } from '../components/console/SettingsSection';
import { SettingRow, SettingRowDivider } from '../components/console/SettingRow';
import { useDirtyRegistration } from '../components/console/SettingsDirtyContext';

export function SlackNotificationsSection() {
  const settingsQuery = trpc.productionSettings.getSlackSettings.useQuery();
  const updateMutation = trpc.productionSettings.updateSlackSettings.useMutation();
  const testMutation = trpc.productionSettings.testSlackWebhook.useMutation();

  const [webhookUrl, setWebhookUrl] = useState('');
  const [messageTemplate, setMessageTemplate] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (settingsQuery.data && !loaded) {
      setMessageTemplate(settingsQuery.data.messageTemplate);
      setEnabled(settingsQuery.data.notificationsEnabled);
      setLoaded(true);
    }
  }, [settingsQuery.data, loaded]);

  const serverTemplate = settingsQuery.data?.messageTemplate ?? '';
  const serverEnabled = settingsQuery.data?.notificationsEnabled ?? false;
  const isDirty =
    loaded && (webhookUrl !== '' || messageTemplate !== serverTemplate || enabled !== serverEnabled);

  useDirtyRegistration(
    'slack',
    isDirty
      ? {
          label: 'Slack notifications',
          save: async () => {
            await updateMutation.mutateAsync({
              slackWebhookUrl: webhookUrl || null,
              slackMessageTemplate: messageTemplate || null,
              slackNotificationsEnabled: enabled,
            });
            setWebhookUrl('');
            await settingsQuery.refetch();
          },
          discard: () => {
            setWebhookUrl('');
            setMessageTemplate(serverTemplate);
            setEnabled(serverEnabled);
          },
        }
      : null,
  );

  const handleTest = () => {
    if (!webhookUrl) {
      toast.error('No webhook URL', { description: 'Enter a webhook URL to test' });
      return;
    }
    testMutation.mutate(
      { webhookUrl },
      {
        onSuccess: () => {
          toast.success('Test successful', { description: 'Check your Slack channel for the test message' });
        },
        onError: (error) => toast.error('Test failed', { description: error.message }),
      },
    );
  };

  const handleResetTemplate = () => {
    if (settingsQuery.data?.defaultTemplate) {
      setMessageTemplate(settingsQuery.data.defaultTemplate);
    }
  };

  return (
    <SettingsSection
      id="slack"
      title="Slack notifications"
      description="Post inventory-error alerts to a Slack webhook."
      icon={MessageSquare}
      scope="org"
    >
      {settingsQuery.isPending ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="space-y-4">
          <SettingRow
            htmlFor="slack-enabled"
            label="Enable notifications"
            description="Send Slack messages when inventory errors are reported"
          >
            <Switch
              id="slack-enabled"
              checked={enabled}
              onCheckedChange={setEnabled}
              disabled={updateMutation.isPending}
              aria-describedby="slack-enabled-status"
            />
            <span id="slack-enabled-status" className="sr-only">
              {enabled ? 'Currently enabled' : 'Currently disabled'}
            </span>
          </SettingRow>

          <SettingRowDivider />

          <div className="space-y-2">
            <Label htmlFor="slack-webhook" className="text-sm font-medium">
              Webhook URL
            </Label>
            <p className="text-sm text-muted-foreground/90">
              {settingsQuery.data?.hasWebhookUrl ? (
                <>
                  Current: <code className="bg-muted px-1 rounded">{settingsQuery.data.webhookUrlMasked}</code>{' '}
                  (enter a new URL to replace it)
                </>
              ) : (
                <>Get your webhook URL from Slack App &gt; Incoming Webhooks</>
              )}
            </p>
            <div className="flex gap-2">
              <Input
                id="slack-webhook"
                type="url"
                placeholder="https://hooks.slack.com/services/..."
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
                className="h-10 flex-1 font-mono text-sm"
              />
              <Button
                variant="outline"
                onClick={handleTest}
                disabled={!webhookUrl || testMutation.isPending}
                className="h-10 min-w-[88px] gap-2"
              >
                {testMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                Test
              </Button>
            </div>
          </div>

          <SettingRowDivider />

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="slack-template" className="text-sm font-medium">
                Message Template
              </Label>
              <Button variant="ghost" onClick={handleResetTemplate} className="h-9 text-sm gap-2 px-3">
                <RefreshCw className="h-4 w-4" />
                Reset to Default
              </Button>
            </div>
            <p className="text-sm text-muted-foreground/90">
              Available variables: <code className="bg-muted px-1 rounded text-xs">{'{location_name}'}</code>{' '}
              <code className="bg-muted px-1 rounded text-xs">{'{sku}'}</code>{' '}
              <code className="bg-muted px-1 rounded text-xs">{'{color}'}</code>{' '}
              <code className="bg-muted px-1 rounded text-xs">{'{size}'}</code>{' '}
              <code className="bg-muted px-1 rounded text-xs">{'{quantity}'}</code>{' '}
              <code className="bg-muted px-1 rounded text-xs">{'{batch_number}'}</code>{' '}
              <code className="bg-muted px-1 rounded text-xs">{'{order_links}'}</code>{' '}
              <code className="bg-muted px-1 rounded text-xs">{'{order_list}'}</code>{' '}
              <code className="bg-muted px-1 rounded text-xs">{'{reporter_name}'}</code>{' '}
              <code className="bg-muted px-1 rounded text-xs">{'{timestamp}'}</code>
            </p>
            <Textarea
              id="slack-template"
              value={messageTemplate}
              onChange={(e) => setMessageTemplate(e.target.value)}
              className="min-h-[180px] font-mono text-sm"
              placeholder="Enter your message template..."
            />
          </div>
        </div>
      )}
    </SettingsSection>
  );
}
